<template>
  <div>
    <template v-if="loading">
      <AppSpinner
        class="page-spinner"
      />
    </template>

    <template v-else-if="!photos || !event">
      There was an error loading photos
    </template>

    <template v-else>
      <div class="flex flex-col xl:flex-row xl:items-center justify-between gap-3 mb-4">
        <!-- Search -->
        <section class="flex">
          <div class="w-[250px] md:w-[345px] xl:w-[300px]">
            <input
              v-model="searchParams.search"
              type="text"
              class="relative border-gray focus:border-secondary focus:ring-0 rounded-r-none border-r-0 py-2 w-full"
              placeholder="Search for photo"
              @keyup.enter="loadPhotos"
            >
          </div>

          <AppDropdown
            class="admin-dropdown context-dropdown w-40"
            :trigger-text="contextText || 'Context'"
          >
            <AppDropdownItem
              class="h-10"
              @click="searchParams.searchBy = null; contextText = 'Context'"
            >
              All
            </AppDropdownItem>

            <template
              v-for="context in photoSearchContext"
              :key="context.value"
            >
              <AppDropdownItem @click="searchParams.searchBy = context.value; contextText = context.label">
                {{ context.label }}
              </AppDropdownItem>
            </template>
          </AppDropdown>
        </section>

        <section class="flex gap-3">
          <!-- Category drop-down -->
          <AppDropdown
            class="admin-dropdown w-40"
            :trigger-text="categoryText || 'Category'"
          >
            <AppDropdownItem
              class="h-10"
              @click="searchParams.category = null; categoryText = 'Category'"
            >
              All
            </AppDropdownItem>

            <template
              v-for="category in event.categories"
              :key="category.id"
            >
              <AppDropdownItem @click="searchParams.category = category.id; categoryText = category.name">
                {{ category.name }}
              </AppDropdownItem>
            </template>
          </AppDropdown>

          <!-- device drop-down -->
          <AppDropdown
            class="admin-dropdown w-40"
            :trigger-text="deviceText || 'Device'"
          >
            <AppDropdownItem
              class="h-10"
              @click="searchParams.device = null; deviceText = 'Device'"
            >
              All
            </AppDropdownItem>

            <template
              v-for="device in devices"
              :key="device.id"
            >
              <AppDropdownItem @click="searchParams.device = device.id; deviceText = device.name">
                {{ device.name }}
              </AppDropdownItem>
            </template>
          </AppDropdown>

          <!-- Resolution drop-down -->
          <AppDropdown
            class="admin-dropdown w-40"
            :trigger-text=" resolutionText || 'Resolution'"
          >
            <AppDropdownItem
              class="h-10"
              @click="searchParams.resolution = null; resolutionText = 'Resolution'"
            >
              All
            </AppDropdownItem>

            <template
              v-for="resolution in resolutions"
              :key="resolution.value"
            >
              <AppDropdownItem @click="searchParams.resolution = resolution.value; resolutionText = resolution.label">
                {{ resolution.label }}
              </AppDropdownItem>
            </template>
          </AppDropdown>
        </section>

        <AppButton
          class="btn btn-solid btn-secondary rounded rounded-e h-10 lg:h-11 w-24"
          @click="loadPhotos"
        >
          Search
        </AppButton>
      </div>

      <!-- Delete Button -->
      <div class="flex justify-between my-4 gap-2">
        <div class="flex gap-3">
          <AppButton
            type="button"
            :disabled="loading || selectedPhotos.length === 0"
            class="text-[12px] xs:text-[14px] btn-solid btn-danger rounded-sm px-3 md:px-3 whitespace-nowrap hover:bg-red-500 w-fit"
            :class="{ 'bg-gray-light' : selectedPhotos.length === 0 }"
            @click="showPhotoDeleteModal = true;"
          >
            Delete Selected Photos
          </AppButton>

          <AppButton
            type="button"
            :disabled="loading || selectedPhotos.length === 0"
            class="text-[12px] xs:text-[14px] text-gray-500 border border-gray-500 rounded-sm px-3 md:px-3 whitespace-nowrap hover:border-secondary hover:bg-secondary hover:text-white w-fit"
            :class="{ 'bg-white' : selectedPhotos.length === 0 }"
            @click="showPhotoCategoriseModal = true;"
          >
            Change Category
          </AppButton>
        </div>

        <AppButton
          type="button"
          class="text-[12px] xs:text-[14px] text-red-600 border border-red-600 rounded-sm px-3 md:px-3 whitespace-nowrap hover:bg-red-500 hover:text-neutral-50 w-fit"
          @click="showPhotoDeleteModal = true; deleteAllPhotos = true"
        >
          <FontAwesomeIcon :icon="faTrashCan" />

          Delete All Photos for this Event
        </AppButton>
      </div>

      <AppTable
        :loading="loading"
        :if-looped-items-exist="photos?.length > 0"
        class="max-w-7xl"
      >
        <template #headings>
          <!-- Select all check box -->
          <th>
            <label class="cursor-pointer flex items-center gap-2">
              <input
                type="checkbox"
                :checked="selectedPhotos.length !== 0 && selectedPhotos.length === photos.length"
                :disabled="photos.length === 0"
                :class="{ 'cursor-pointer': photos.length > 0 }"
                @click="selectAllPhotos"
              >
            </label>
          </th>

          <th>Photo</th>
          <th>Ulid</th>
          <th>Visual Score</th>
          <th>Category</th>
          <th>Device</th>
          <th>Bib numbers</th>
          <th />
        </template>

        <template #rows>
          <tr
            v-for="photo in photos"
            :key="photo.id"
            class="h-16"
          >
            <td>
              <input
                type="checkbox"
                :checked="selectedPhotos.includes(photo.id) || deleteAllPhotos"
                :value="photo.id"
                class="!cursor-pointer"
                @change="togglePhotoSelected"
              >
            </td>
            <td class="text-gray-900 whitespace-nowrap w-1 h-full">
              <AppButton
                class="p-0 border-0"
                @click="photoToView = photo"
              >
                <img
                  loading="lazy"
                  class="h-full object-cover object-center cursor-zoom-in"
                  :src="photo.thumbnail.url"
                  alt="photo thumbnail"
                >
              </AppButton>
            </td>
            <td>
              <code
                class="font-mono text-sm text-gray-700 font-medium rounded border border-gray-200 bg-gray-100 px-1.5 py-1 block w-full text-center group relative duration-150"
                :class="isSupported ? 'cursor-copy' : ''"
                @click="isSupported ? copy(photo.ulid) : null"
              >
                {{ photo.ulid }}
                <span
                  class="opacity-0 block invisible group-hover:visible group-hover:opacity-100 duration-150 absolute left-1/2 -translate-x-1/2 -top-[110%] bg-white rounded p-1 font-sans z-10 text-xs text-gray-500 border border-gray-200"
                >
                  <FontAwesomeIcon
                    v-show="isSupported && !copied"
                    :icon="faCopy"
                  />
                  {{ isSupported && copied && text === photo.ulid ? 'copied!' : 'copy ULID' }}
                </span>
              </code>
            </td>

            <td>
              {{ photo.visualScore || '-' }}
            </td>

            <td>
              {{ photo.category?.name || '-' }}
            </td>

            <td>
              {{ photo.device?.name || '-' }}
            </td>

            <td>
              <span
                v-if="photo.bibNumbers.length"
                class="flex flex-wrap gap-1.5"
              >
                <template
                  v-for="bibNumber in photo.bibNumbers"
                  :key="bibNumber"
                >
                  <span class="text-xs p-0.5 leading-none rounded border border-gray-100 bg-gray-50 font-mono">{{
                    bibNumber
                  }}</span>
                </template>
              </span>

              <template v-else>
                -
              </template>
            </td>

            <td>
              <span
                class="cursor-pointer underline text-red-400"
                @click="showPhotoDeleteModal = true; photoToDelete = photo"
              >
                Delete
              </span>
            </td>
          </tr>
        </template>
      </AppTable>
    </template>

    <AdminPagination
      v-if="!loading && photos"
      target-id="topOfPage"
      :items-per-page="paginationData?.itemsPerPage"
      :current-page="paginationData?.page"
      :total-items="paginationData?.total"
      @change="handlePageChange"
    />

    <Transition name="fade">
      <AppModal
        v-if="photoToView"
        class="view-photo-modal"
        size="lg"
        @close="photoToView = null"
      >
        <img
          loading="lazy"
          class="object-cover object-center mb-6 max-h-[60vh] w-full mx-auto"
          :src="photoToView.thumbnail.url"
          alt="photo thumbnail"
        >

        <div class="flex justify-between gap-2">
          <AppButton
            class="btn-outline btn-secondary w-full max-w-md text-[15px]"
            @click="photoToView = null"
          >
            Close
          </AppButton>

          <a
            class="btn btn-solid btn-secondary w-full max-w-md text-[15px]"
            :href="photoToView.originalPhotoUrl"
            target="_blank"
          >
            Download Photo
          </a>
        </div>
      </AppModal>
    </Transition>

    <Transition name="fade">
      <template v-if="showPhotoDeleteModal">
        <DeleteModal
          :title="photoToDelete ? 'Photo' : 'Photos'"
          :text="deleteModalText"
          :entity-name="photoToDelete ? photoToDelete.ulid : null"
          :error="error"
          :processing="isDeleting"
          @delete="handleDeletePhotos"
          @close="handleCloseModal"
        />
      </template>
    </Transition>

    <Transition name="fade">
      <template v-if="showPhotoCategoriseModal">
        <AppModal
          :disabled="isSubmitting"
          class="bulk-category-modal"
          @close="() => { showPhotoCategoriseModal = null; error = null }"
        >
          <BulkCategoryForm
            :categories="event.categories"
            :is-submitting="isSubmitting"
            @confirm="handleBulkCategorisePhotos"
            @close="() => { showPhotoCategoriseModal = null }"
          />
        </AppModal>
      </template>
    </Transition>
  </div>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import { computed, inject, reactive, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { AppButton, AppDropdown, AppDropdownItem, AppModal, AppSpinner, AppTable } from '@/components/app';
import { AdminPagination } from '@/components/app/admin';
import { DeleteModal } from '@/components/modals';

import { faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useClipboard } from '@vueuse/core';

import { useHttp } from '@/composables';
import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { BulkCategoryForm } from '@/components/forms';

const { text, copy, copied, isSupported } = useClipboard();

const photoSearchContext = [
  { label: 'Bib Number', value: 'bibNumber' },
  { label: 'ULID', value: 'ulid' },
];

const resolutions = [
  { label: 'Low', value: 'Low' },
  { label: 'Medium', value: 'Medium' },
  { label: 'High', value: 'High' },
];

const route = useRoute();

const loading = ref(true);
const event = inject('event');
const photos = ref(null);
const devices = ref([]);

const photoToView = ref(null);
const photoToDelete = ref(null);
const showPhotoDeleteModal = ref(false);
const showPhotoCategoriseModal = ref(false);
const isDeleting = ref(false);
const isSubmitting = ref(false);
const error = ref(null);

const contextText = ref(null);
const categoryText = ref(null);
const deviceText = ref(null);
const resolutionText = ref(null);

const searchParams = reactive({
  page: 1,
  itemsPerPage: 100,
  searchBy: 'bibNumber',
  search: null,
  category: null,
  bibNumber: null,
  ulid: null,
  device: null,
  resolution: null,
});

const paginationData = ref(null);
const selectedPhotos = ref([]);
const deleteAllPhotos = ref(false);

const loadPhotos = async () => {
  loading.value = true;

  if (searchParams.searchBy === 'ulid') {
    searchParams.ulid = searchParams.search;
  }

  const response = await useHttp().get(`/events/${route.params.slug}/photos`, searchParams);

  if (response.statusCode === HttpStatusCode.Ok) {
    photos.value = response.data.items;
    paginationData.value = response.data.pagination;

    // Reset the values of selected photos to null or false as appropriate.
    photoToDelete.value = null;
    selectedPhotos.value = [];
    showPhotoDeleteModal.value = false;
    deleteAllPhotos.value = false;
    searchParams.ulid = null;
  }

  loading.value = false;
};

// Returns all the devices as devices used on the photos may no longer be assigned to the event
const loadDevices = async () => {
  const response = await useHttp().get('/devices');

  if (response.statusCode === HttpStatusCode.Ok) {
    devices.value = response.data.items;
  }
};

const handleBulkCategorisePhotos = async (selectedCategory) => {
  error.value = null;
  isSubmitting.value = true;

  const data = {
    photoIds: selectedPhotos.value,
    category: selectedCategory,
  };

  const response = await useHttp().post(`/events/${route.params.slug}/photos/bulk/categorise`, data);

  isSubmitting.value = false;

  if (response.statusCode === HttpStatusCode.Ok) {

    handleCloseModal();

    await loadPhotos();
  } else {
    error.value = 'Something went wrong. Please try again.';
  }
};

const handlePageChange = page => {
  searchParams.page = page;

  loadPhotos();
};

const deleteModalText = computed(() => {
  if (photoToDelete.value) {
    return 'Are you sure you want to delete this photo from this event?';
  } else if (deleteAllPhotos.value) {
    return 'Are you sure you want to delete all photos for this event?';
  }

  return 'Are you sure you want to delete the selected photos?';
});

const handleDeletePhotos = async () => {
  error.value = null;
  isDeleting.value = true;

  const data = {
    photoIds: photoToDelete.value ? [photoToDelete.value.id] : selectedPhotos.value,
    deleteAll: deleteAllPhotos.value,
  };

  const response = await useHttp().post(`/events/${route.params.slug}/photos/bulk/delete`, data);

  isDeleting.value = false;

  if (response.statusCode === HttpStatusCode.Ok) {
    // Reset the selected photos array only if the photos were being bulk deleted
    if (!photoToDelete.value) {
      selectedPhotos.value = [];
    }

    handleCloseModal();

    await loadPhotos();
  } else {
    error.value = 'Something went wrong. Please try again.';
  }
};

/*
 * Adds the photo's ID to the `selectedPhotos` array if it hasn't already been selected. If the checkbox is already selected,
 * remove the photo ID from the `selectedPhotos` array.
 */
const togglePhotoSelected = event => {
  const photoId = parseInt(event.target.value);
  const checked = event.target.checked;

  if (checked && !selectedPhotos.value.includes(photoId)) {
    selectedPhotos.value.push(photoId);
  } else if (selectedPhotos.value.includes(photoId)) {
    selectedPhotos.value = selectedPhotos.value.filter(id => photoId !== id);
  }
};

// Marks all photos as selected for bulk actions
const selectAllPhotos = () => {
  if (selectedPhotos.value.length < photos.value.length) {
    photos.value.forEach(photo => {
      if (!selectedPhotos.value.includes(photo.id)) {
        selectedPhotos.value.push(photo.id);
      }
    });
  } else if (selectedPhotos.value.length === photos.value.length) {
    selectedPhotos.value = [];
  }
};

const handleCloseModal = () => {
  photoToDelete.value = null;
  showPhotoDeleteModal.value = false;
  showPhotoCategoriseModal.value = false;
  error.value = null;
  deleteAllPhotos.value = false;
};

loadPhotos();

watch(event, (value) => {
  if (value.id) {
    loadDevices();
  }
}, { immediate: true });

</script>
